import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { MapPin, LocateFixed, Loader2, Search, Star, Trash2, Check, Plus } from "lucide-react";
import { locationApi } from "@/lib/api";
import { useLocationStore } from "@/store/locationStore";

export default function LocationPickerDialog({ open, onOpenChange }) {
  const saved = useLocationStore((s) => s.saved);
  const activeLabel = useLocationStore((s) => s.label);
  const detect = useLocationStore((s) => s.detect);
  const setActive = useLocationStore((s) => s.setActive);
  const addSaved = useLocationStore((s) => s.addSaved);
  const removeSaved = useLocationStore((s) => s.removeSaved);

  const [detecting, setDetecting] = useState(false);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null); // {label, city, area, pincode, coords}
  const [saveName, setSaveName] = useState("");
  const sessionToken = useRef(null);
  const debounceRef = useRef(null);

  const close = () => {
    setQuery("");
    setSuggestions([]);
    setPicked(null);
    setSaveName("");
    onOpenChange(false);
  };

  const useCurrent = async () => {
    setDetecting(true);
    const res = await detect();
    setDetecting(false);
    if (res.ok) {
      toast.success(`Location set to ${res.label}`);
      close();
    } else if (res.error === "denied") {
      toast.error("Location blocked. Enable location access in your browser settings.");
    } else if (res.error === "unsupported") {
      toast.error("Your browser doesn't support location.");
    } else {
      toast.error("Couldn't fetch your location. Please try again.");
    }
  };

  const onQueryChange = (text) => {
    setQuery(text);
    setPicked(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!text || text.length < 3) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await locationApi.autocomplete(text, sessionToken.current, null);
        if (res.data?.success) {
          setSuggestions(res.data.predictions || []);
          if (res.data.session_token) sessionToken.current = res.data.session_token;
        }
      } catch {
        setSuggestions([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  const selectSuggestion = async (s) => {
    setQuery(s.description || s.main_text || "");
    setSuggestions([]);
    try {
      const det = await locationApi.placeDetails(s.place_id, sessionToken.current);
      sessionToken.current = null;
      const place = det.data?.place;
      if (place?.latitude && place?.longitude) {
        let city = null, area = null, pincode = null;
        try {
          const geo = await locationApi.reverseGeocode(place.latitude, place.longitude);
          const g = geo.data && !geo.data.error ? geo.data : {};
          city = g.city || null;
          area = g.area || g.locality || null;
          pincode = g.pincode || null;
        } catch { /* ignore */ }
        // Fallback: derive a city name from the Places result so city-matching
        // still works (e.g. a lab registered in that city with no coordinates).
        if (!city) city = s.main_text || (s.description || "").split(",")[0] || null;
        const label = place.formatted_address || s.description;
        const loc = { label, city, area, pincode, coords: { latitude: place.latitude, longitude: place.longitude } };
        setPicked(loc);
        setActive(loc);
        toast.success("Location updated");
      } else {
        toast.error("Couldn't pinpoint that address. Try another.");
      }
    } catch {
      toast.error("Couldn't fetch that location.");
    }
  };

  const savePicked = () => {
    if (!picked || !saveName.trim()) return;
    addSaved({ name: saveName.trim(), ...picked });
    toast.success(`Saved "${saveName.trim()}"`);
    setSaveName("");
    close();
  };

  const switchTo = (loc) => {
    setActive(loc);
    toast.success(`Switched to ${loc.name || loc.label}`);
    close();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent className="rounded-3xl max-w-md" data-testid="location-picker-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-primary" /> Choose location
          </DialogTitle>
        </DialogHeader>

        {activeLabel && (
          <p className="text-xs text-muted-foreground -mt-1">Current: <span className="font-medium text-foreground">{activeLabel}</span></p>
        )}

        <Button variant="outline" className="w-full rounded-xl justify-start" onClick={useCurrent} disabled={detecting} data-testid="use-current-location">
          {detecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <LocateFixed className="h-4 w-4 text-primary" />}
          Use my current location
        </Button>

        <div className="relative">
          <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-3" />
          <Input
            className="rounded-xl pl-9"
            placeholder="Search area, city or landmark…"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            data-testid="location-search-input"
          />
          {searching && <Loader2 className="h-4 w-4 animate-spin text-primary absolute right-3 top-3" />}
          {suggestions.length > 0 && (
            <div className="absolute z-20 left-0 right-0 mt-1 rounded-xl border border-border bg-card shadow-lg max-h-56 overflow-y-auto">
              {suggestions.map((s, i) => (
                <button
                  key={s.place_id || i}
                  onClick={() => selectSuggestion(s)}
                  data-testid={`location-suggestion-${i}`}
                  className="flex items-start gap-2 w-full text-left px-3 py-2.5 hover:bg-muted/60 transition-colors"
                >
                  <MapPin className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{s.main_text || s.description}</p>
                    {s.secondary_text && <p className="text-xs text-muted-foreground truncate">{s.secondary_text}</p>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Save the freshly-picked location */}
        {picked && (
          <div className="rounded-xl bg-muted/40 p-3 space-y-2" data-testid="save-location-box">
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Check className="h-3.5 w-3.5 text-success" /> {picked.label}
            </p>
            <div className="flex gap-2">
              <Input
                className="rounded-xl h-9"
                placeholder="Save as (e.g. Home, Sanam)"
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                data-testid="save-location-name"
              />
              <Button className="rounded-xl h-9 shrink-0" onClick={savePicked} disabled={!saveName.trim()} data-testid="save-location-btn">
                <Plus className="h-4 w-4" /> Save
              </Button>
            </div>
          </div>
        )}

        {/* Saved locations to switch between */}
        {saved.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-muted-foreground mb-2">Saved locations</p>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {saved.map((loc) => (
                <div key={loc.id} className="flex items-center gap-2 rounded-xl border border-border p-2.5" data-testid={`saved-location-${loc.id}`}>
                  <div className="h-8 w-8 rounded-lg bg-accent flex items-center justify-center shrink-0">
                    <Star className="h-4 w-4 text-primary" />
                  </div>
                  <button className="flex-1 min-w-0 text-left" onClick={() => switchTo(loc)} data-testid={`switch-location-${loc.id}`}>
                    <p className="text-sm font-medium truncate">{loc.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{loc.label || loc.city}</p>
                  </button>
                  <button onClick={() => { removeSaved(loc.id); toast.success("Removed"); }} className="p-1.5 text-muted-foreground hover:text-destructive" data-testid={`delete-location-${loc.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
